import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generateAudio, checkTaskStatus } from '../../services/audioApi';
import { 
    Mic, Download, Loader2, AlertCircle, 
    CheckCircle, PlayCircle, RefreshCw 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const GenerateAudio = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // State
    const [text, setText] = useState('');
    const [voice, setVoice] = useState('alloy');
    const [format, setFormat] = useState('WAV');
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressValue, setProgressValue] = useState(0);
    const [progressStep, setProgressStep] = useState('');
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    // Voice Options Map
    const voiceOptions = [
        { id: "alloy", label: "Alloy (Neutral)" },
        { id: "echo", label: "Echo (Male)" },
        { id: "fable", label: "Fable (British Male)" },
        { id: "onyx", label: "Onyx (Deep Male)" },
        { id: "nova", label: "Nova (Female)" },
        { id: "shimmer", label: "Shimmer (Female)" }
    ];

    const getProgressLabel = (step) => {
        const map = {
            'generating': t('generateAudio.steps.generating'),
            'converting': t('generateAudio.steps.converting'),
            'saving': t('generateAudio.steps.saving')
        };
        return map[step] || t('generateAudio.steps.processing');
    };

    // Cleanup
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);



    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result?.result || data.result;
                        
                        if (payload.new_credits !== undefined) setCredits(payload.new_credits);
                        
                        setResult({
                            url: payload.download_url,
                            filename: payload.filename
                        });
                        setProgressValue(100);
                        setIsProcessing(false);

                    } else if (data.state === 'FAILURE' || data.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('generateAudio.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
    }, [taskId, isProcessing, t, setCredits]);

    const handleGenerate = async () => {
        if (!text.trim()) {
            setError(t('generateAudio.errors.missingText'));
            return;
        }

        setIsProcessing(true);
        setError(null);
        setResult(null);
        setProgressValue(10);

        try {
            const response = await generateAudio(text, voice, format);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setResult(null);
        setError(null);
        setTaskId(null);
        setProgressValue(0);
    };

    // --- Components ---

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : result ? (
                <ResultView 
                result={result}
                handleReset={handleReset}
                t={t}
                />
            ) : (
                <InputView 
                text={text}
                setText={setText}
                voice={voice}
                setVoice={setVoice}
                format={format}
                setFormat={setFormat}
                isProcessing={isProcessing}
                handleGenerate={handleGenerate}
                error={error}
                progressValue={progressValue}
                voiceOptions={voiceOptions}
                t={t}
                progressStep={progressStep}
                getProgressLabel={getProgressLabel}
                />
            )}
        </div>
    );
};

// const voiceOptions = [
//     { id: "alloy", label: "Alloy (Neutral)" },
//     { id: "echo", label: "Echo (Male)" },
//     { id: "fable", label: "Fable (British Male)" },
//     { id: "onyx", label: "Onyx (Deep Male)" },
//     { id: "nova", label: "Nova (Female)" },
//     { id: "shimmer", label: "Shimmer (Female)" }
// ];


const InputView = ({ text, setText, voice, setVoice, format, setFormat, isProcessing, handleGenerate, error, progressValue, voiceOptions, t , progressStep, getProgressLabel}) => (
    <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-2">{t('generateAudio.title')}</h2>
        <p className="mt-2 text-brand-dark">{t('generateAudio.description')}</p>
        
        <div className="mt-8 space-y-6">
            
            {/* Text Input */}
            <div>
                <label className="block text-sm font-medium mb-2 text-brand-dark">
                    {t('generateAudio.scriptLabel')}
                </label>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={5000}
                    rows={6}
                    className="w-full p-4 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent bg-white text-slate-800"
                    placeholder={t('generateAudio.placeholder')}
                    disabled={isProcessing}
                />
                <div className="flex justify-end mt-1">
                    <span className={`text-xs ${text.length > 4500 ? 'text-red-500' : 'text-slate-500'}`}>
                        {text.length}/5000
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Voice Selection */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('generateAudio.voiceLabel')}
                    </label>
                    <select 
                        value={voice} 
                        onChange={(e) => setVoice(e.target.value)}
                        disabled={isProcessing}
                        className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-800"
                    >
                        {voiceOptions.map(v => (
                            <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                    </select>
                </div>

                {/* Format Selection */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('generateAudio.formatLabel')}
                    </label>
                    <select 
                        value={format} 
                        onChange={(e) => setFormat(e.target.value)}
                        disabled={isProcessing}
                        className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-800"
                    >
                        <option value="WAV">WAV</option>
                        <option value="MP3">MP3</option>
                        <option value="OGG">OGG</option>
                    </select>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-center">
                    <AlertCircle className="mr-2" size={20} />
                    {error}
                </div>
            )}

            <button
                onClick={handleGenerate}
                disabled={isProcessing || !text.trim()}
                className="w-full md:w-auto px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
                {isProcessing ? (
                    <>
                        <Loader2 className="animate-spin mr-2" size={20} />
                        {t('generateAudio.buttons.generating')}
                    </>
                ) : (
                    <>
                        <PlayCircle className="mr-2" size={20} />
                        {t('generateAudio.buttons.generate')}
                    </>
                )}
            </button>

            {isProcessing && (
                <div className="mt-4">
                    <div className="flex justify-between text-sm text-slate-600 mb-2">
                        <span>{getProgressLabel(progressStep)}</span>
                        <span>{progressValue}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div 
                            className="bg-brand-orange h-2.5 rounded-full transition-all duration-500 ease-out" 
                            style={{ width: `${progressValue}%` }}
                        ></div>
                    </div>
                </div>
            )}
        </div>
    </div>
);


const ResultView = ({ result, handleReset, t }) => (
        <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-6 flex items-center text-green-800">
                <CheckCircle className="mr-2" />
                {t('generateAudio.success')}
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-200 p-8 text-center">
                <h3 className="text-xl font-semibold mb-6 text-brand-dark">{t('generateAudio.playerTitle')}</h3>
                
                <div className="flex justify-center mb-8">
                    <audio controls className="w-full max-w-md" src={result.url}>
                        Your browser does not support the audio element.
                    </audio>
                </div>

                <div className="flex justify-center gap-4">
                    <a 
                        href={result.url} 
                        download={result.filename}
                        className="flex items-center gap-2 bg-brand-orange text-white px-6 py-2 rounded-md hover:bg-orange-600 transition"
                    >
                        <Download size={20} />
                        {t('generateAudio.buttons.download')}
                    </a>
                    
                    <button 
                        onClick={handleReset}
                        className="flex items-center gap-2 border border-slate-300 text-slate-700 px-6 py-2 rounded-md hover:bg-slate-50 transition"
                    >
                        <RefreshCw size={20} />
                        {t('generateAudio.buttons.new')}
                    </button>
                </div>
            </div>
        </div>
    );
export default GenerateAudio;
